import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Shield, TrendingDown, TrendingUp, Lock, ExternalLink } from 'lucide-react';

export default function ForUnderwriters() {
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="for-underwriters-page">
      <Helmet>
        <title>For Underwriters: Kojiru | The Instrument That Makes Agents Insurable</title>
        <meta name="description" content="Autonomous AI agents without verifiable capital and verifiable consent are an uninsurable liability. Kojiru's KYPA credential provides both. Loss-ratio math for cyber, E&O, and agent-liability underwriters." />
        <link rel="alternate" type="application/ld+json" href="/kojiru-schema.jsonld" />
        <link rel="alternate" type="application/json" href="/.well-known/kypa" title="KYPA Protocol Manifest" />
      </Helmet>

      <Navbar />

      {/* HERO */}
      <section className="pt-24 pb-10 px-6 border-b border-white/10">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="text-[10px] font-bold tracking-[2.4px] text-red-400 mb-4" data-testid="hero-eyebrow">
              FOR CYBER, E&O, AND AGENT-LIABILITY UNDERWRITERS
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-6 tracking-tight" data-testid="hero-headline">
              An autonomous agent without verifiable capital<br />
              and verifiable consent<br />
              <span className="text-red-400">is an uninsurable liability.</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl" data-testid="hero-body">
              Underwriting prices risk from two ingredients: <strong className="text-white">capital that can pay if the promise fails</strong>, and <strong className="text-white">a signature that proves the right human authorized the action</strong>. When neither exists, the exposure is unbounded and no rational underwriter can quote it. Kojiru's KYPA credential provides both by construction. This page translates the primitive into the math a cyber, E&O, or agent-liability carrier needs to write a book.
            </p>
          </motion.div>
        </div>
      </section>

      {/* THE UNDERWRITING PROBLEM */}
      <section className="py-14 px-6" data-testid="underwriting-problem-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-slate-400 mb-3">THE UNDERWRITING PROBLEM, IN ONE PARAGRAPH</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">Two ingredients. Both must be verifiable at claim time.</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="border border-red-500/25 rounded-xl p-5 bg-red-500/[0.04]" data-testid="ingredient-capital">
              <div className="text-[10px] font-bold tracking-widest text-red-300 mb-3">INGREDIENT 1</div>
              <div className="text-xl font-bold text-white mb-3">Verifiable Capital</div>
              <p className="text-sm text-slate-300 leading-relaxed mb-3">
                If the agent causes a loss, can the underwriter recover? A promise from an LLM operator to <em>"make it right"</em> is not capital. A pledged, public USDC balance visible on-chain is.
              </p>
              <p className="text-xs text-slate-500 italic">
                Without this ingredient, the underwriter has to price for infinite tail risk. Every quote is priced out of the market.
              </p>
            </div>
            <div className="border border-red-500/25 rounded-xl p-5 bg-red-500/[0.04]" data-testid="ingredient-consent">
              <div className="text-[10px] font-bold tracking-widest text-red-300 mb-3">INGREDIENT 2</div>
              <div className="text-xl font-bold text-white mb-3">Verifiable Consent</div>
              <p className="text-sm text-slate-300 leading-relaxed mb-3">
                When a claim comes in, did the right human actually authorize the action the agent took? A chat log is not proof. A cryptographic signature over a mandate envelope is.
              </p>
              <p className="text-xs text-slate-500 italic">
                Without this ingredient, every claim becomes a he-said-she-said between operator, cardholder, and issuer. Loss adjustment costs eat the entire premium.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW KYPA FIXES IT */}
      <section className="py-14 px-6 bg-slate-900/60 border-y border-white/10" data-testid="how-kypa-fixes-it">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-emerald-300 mb-3">HOW THE KYPA CREDENTIAL PROVIDES BOTH</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">Uninsurable &rarr; insurable in one primitive.</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="border border-emerald-500/25 rounded-xl p-5 bg-emerald-500/[0.05]">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-5 h-5 text-emerald-400" />
                <div className="text-[10px] font-bold tracking-widest text-emerald-300">CAPITAL SOLVED BY</div>
              </div>
              <div className="text-lg font-bold text-white mb-3">The slashable pledge on Base L2</div>
              <p className="text-sm text-slate-300 leading-relaxed">
                Every KYPA-registered agent pledges USDC as slashable collateral. The pledge is a public commitment signal the underwriter can read on-chain when pricing an operator. It is not insurance, not a surety bond, and not a promise of repayment to any party.
              </p>
              <div className="mt-3 text-xs text-emerald-300 font-mono">
                capital exposure = max(0, loss_amount &minus; pledge_balance)
              </div>
            </div>
            <div className="border border-emerald-500/25 rounded-xl p-5 bg-emerald-500/[0.05]">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-emerald-400" />
                <div className="text-[10px] font-bold tracking-widest text-emerald-300">CONSENT SOLVED BY</div>
              </div>
              <div className="text-lg font-bold text-white mb-3">The EIP-712 signed mandate</div>
              <p className="text-sm text-slate-300 leading-relaxed">
                Every KYPA credential is an EIP-712 envelope signed by the agent operator at issuance, bound to a specific pledge, scoped by amount, category, and recipient allow-list. At claim time, the underwriter presents the signed envelope as proof of exact consent. No chat log. No verbal authorization. Cryptographic evidence.
              </p>
              <div className="mt-3 text-xs text-emerald-300 font-mono">
                loss-adjustment cost &rarr; O(1) signature check
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE LOSS-RATIO MATH */}
      <section className="py-14 px-6" data-testid="underwriter-math-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">THE LOSS-RATIO MATH</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">First-principles pricing for a 1M-cardholder book.</h2>
          <p className="text-slate-300 leading-relaxed mb-6 max-w-3xl">
            Standard actuarial pass at a hypothetical agent-liability book covering 1&nbsp;million cardholders. Activation and error rates below are conservative defaults; substitute your own claims history where you have it.
          </p>
          <div className="overflow-x-auto border border-white/10 rounded-xl">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-white/5">
                <tr><td className="p-3 text-slate-400 w-2/3">Cardholders covered</td><td className="p-3 text-white font-mono text-right">1,000,000</td></tr>
                <tr><td className="p-3 text-slate-400">Activation rate &times; actions/yr</td><td className="p-3 text-white font-mono text-right">25% &times; 12</td></tr>
                <tr><td className="p-3 text-slate-400">Out-of-scope error rate (unpledged agents)</td><td className="p-3 text-white font-mono text-right">0.1%</td></tr>
                <tr className="bg-red-500/[0.05]"><td className="p-3 text-red-200 font-bold">Annual dispute exceptions (unpledged)</td><td className="p-3 text-red-200 font-mono text-right font-bold">3,000</td></tr>
                <tr><td className="p-3 text-slate-400">Avg loss per exception</td><td className="p-3 text-white font-mono text-right">$150</td></tr>
                <tr className="bg-red-500/[0.08]"><td className="p-3 text-red-100 font-bold">Gross annual losses (unpledged book)</td><td className="p-3 text-red-100 font-mono text-right font-bold">$450,000</td></tr>
                <tr><td className="p-3 text-slate-400">Avg loss-adjustment cost per claim</td><td className="p-3 text-white font-mono text-right">$25–$50</td></tr>
                <tr className="bg-red-500/[0.05]"><td className="p-3 text-red-200 font-bold">Annual LAE (unpledged)</td><td className="p-3 text-red-200 font-mono text-right font-bold">$75K–$150K</td></tr>
                <tr><td className="p-3 text-slate-400">Loss + LAE (unpledged)</td><td className="p-3 text-red-300 font-mono text-right">$525K–$600K</td></tr>
                <tr className="bg-emerald-500/[0.05]"><td className="p-3 text-emerald-200 font-bold">With KYPA: verify refuses at authorization (est. 95% of losses prevented)</td><td className="p-3 text-emerald-200 font-mono text-right font-bold">$23K–$30K residual loss</td></tr>
                <tr className="bg-emerald-500/[0.05]"><td className="p-3 text-emerald-200 font-bold">With KYPA, pledge visible as a pricing input (no recovery assumed)</td><td className="p-3 text-emerald-200 font-mono text-right font-bold">&lt; $5K</td></tr>
                <tr className="bg-emerald-500/[0.08]"><td className="p-3 text-white font-bold">Residual insured exposure per 1M cardholders</td><td className="p-3 text-emerald-300 font-mono text-right font-bold">under $5K annually</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed mt-4 italic max-w-3xl">
            The 95% prevention rate reflects verify-refusal of authorized-but-out-of-scope transactions <em>before</em> the payment rail is consulted. This model assumes no recovery from the pledge. The pledge is a public commitment signal, not a source of repayment. Illustrative only.
          </p>
        </div>
      </section>

      {/* TWO PRODUCTS */}
      <section className="py-14 px-6 bg-slate-900/60 border-y border-white/10" data-testid="two-products-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-300 mb-3">TWO PRODUCTS UNDERWRITERS CAN BUILD ON THIS PRIMITIVE</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">Same primitive. Two markets.</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="border border-white/10 rounded-xl p-5 bg-black/40">
              <TrendingUp className="w-5 h-5 text-cyan-400 mb-3" />
              <div className="text-lg font-bold text-white mb-2">Agent-Operator E&O</div>
              <p className="text-sm text-slate-400 leading-relaxed mb-3">
                Covers the operator of an autonomous agent for errors and omissions above the pledge balance. The pledge is the retention; the policy attaches at pledge exhaustion. The KYPA envelope is the proof-of-authority record for every covered action.
              </p>
              <div className="text-xs text-slate-500 italic">
                Distribution: sold through agent-platform BD (frontier AI labs and cloud hyperscalers, Kojiru KYPA Directory).
              </div>
            </div>
            <div className="border border-white/10 rounded-xl p-5 bg-black/40">
              <TrendingDown className="w-5 h-5 text-cyan-400 mb-3" />
              <div className="text-lg font-bold text-white mb-2">Merchant Cyber (Agent-Traffic Rider)</div>
              <p className="text-sm text-slate-400 leading-relaxed mb-3">
                Covers merchants who accept agent-mediated transactions for losses attributable to authorized-but-out-of-scope agent behavior. The KYPA verify log is the proof-of-loss record. Premiums drop dramatically for merchants who verify KYPA at checkout.
              </p>
              <div className="text-xs text-slate-500 italic">
                Distribution: rider on existing cyber policies, priced against KYPA verify-rate telemetry.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-6 border-t border-white/10" data-testid="cta-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-slate-500 mb-4">HOW TO ENGAGE</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-5 leading-tight">
            Underwriting starts with a look at the pledge.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-4">
            Every KYPA-registered agent's pledge balance is queryable on-chain in one call. Every KYPA credential envelope is a self-verifying signature. The underwriting due-diligence pack for a book of agent-operators is a query script, not a document review.
          </p>
          <p className="text-slate-300 leading-relaxed mb-6">
            KYPA facilitator integration brief: <a href="/downloads/kypa-facilitator-integration-brief.pdf?v=17" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:underline">kypa-facilitator-integration-brief.pdf</a>. Live pledge queries against the mainnet Hook: <Link to="/proofs?v=17" className="text-cyan-300 hover:underline">/proofs</Link>. Full protocol spec at <Link to="/kypa/instrument?v=17" className="text-cyan-300 hover:underline">/kypa/instrument</Link>.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="/downloads/kypa-facilitator-integration-brief.pdf?v=17" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 rounded-md px-4 py-2" data-testid="brief-download">
              Integration brief <ExternalLink className="w-3 h-3" />
            </a>
            <Link to="/for-issuers?v=17" className="inline-flex items-center gap-2 text-sm font-bold text-amber-300 hover:text-amber-200 border border-amber-500/30 rounded-md px-4 py-2" data-testid="cross-link-issuers">
              For issuing programs &rarr;
            </Link>
            <Link to="/for-merchants?v=17" className="inline-flex items-center gap-2 text-sm font-bold text-slate-300 hover:text-white border border-white/20 rounded-md px-4 py-2" data-testid="cross-link-merchants">
              For merchants &rarr;
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
